import { UserPhoto } from "@/src/models/user.model";
import { GallerySlash } from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, TouchableOpacity, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import Lightbox from "./light-box";
import PhotoGrid from "./photo-grid";

export type ProfilePhotoGridProps = Readonly<{
  photos: readonly UserPhoto[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  isOwnProfile?: boolean;
}>;

const SKELETON_ROWS = [
  { key: "full", count: 1, flexClass: "w-full", aspectRatio: 1 / 0.52 },
  { key: "half", count: 2, flexClass: "flex-1", aspectRatio: 1 / 0.78 },
  { key: "third", count: 3, flexClass: "flex-1", aspectRatio: 1 },
] as const;

const PhotoGridSkeleton = () => {
  const { t } = useTranslation();
  return (
    <View className="flex-1">
      <View className="mx-4 mt-4 mb-3">
        <Text className="text-base font-bold text-onyx">
          {t("LABEL_PHOTOS")}
        </Text>
      </View>
      <View className="h-px bg-surface-variant" />
      <View className="gap-0.5 mt-0.5">
        {SKELETON_ROWS.map(({ key, count, flexClass, aspectRatio }) => (
          <View key={key} className="flex-row gap-0.5">
            {Array.from({ length: count }).map((_, i) => (
              <View
                key={i}
                className={`bg-surface-variant rounded-sm ${flexClass}`}
                style={{ aspectRatio }}
              />
            ))}
          </View>
        ))}
      </View>
    </View>
  );
};

const ProfilePhotoGrid = ({
  photos,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
  isOwnProfile,
}: ProfilePhotoGridProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { themeVars } = useTheme();
  const { t } = useTranslation();

  const photoCountLabel = t(
    photos.length === 1 ? "LABEL_PHOTO" : "LABEL_PHOTOS",
  );

  if (isLoading) return <PhotoGridSkeleton />;

  if (isError) {
    return (
      <View className="flex-1 items-center justify-center gap-3">
        <Text className="text-sm text-slate">
          {t("ERROR_COULD_NOT_LOAD_PHOTOS")}
        </Text>
        <TouchableOpacity
          onPress={onRetry}
          activeOpacity={0.7}
          className="rounded-full border border-primary px-5 py-2 hover:bg-ash-3 hover:rounded-full"
        >
          <Text className="text-primary text-sm font-medium">
            {t("BTN_TRY_AGAIN")}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!photos.length) {
    return (
      // p-10 matches the videos empty state; without it this sat flush against
      // the card edges while the other two tabs had breathing room.
      <View className="flex-1 items-center justify-center gap-2 p-10">
        <GallerySlash size={40} color={themeVars["--color-slate-default"] as string} />
        <Text className="text-base font-semibold text-onyx">
          {t("LABEL_NO_PHOTOS_YET")}
        </Text>
        <Text className="text-sm text-slate text-center">
          {t("LABEL_PHOTOS_EMPTY_DESC")}
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1">
      <View className="mx-4 mt-4 mb-3">
        <Text className="text-base font-bold text-onyx">
          {t("LABEL_PHOTOS")}
        </Text>
        <Text className="text-xs text-slate mt-0.5">
          {photos.length} {photoCountLabel}
        </Text>
      </View>
      <View className="h-px bg-surface-variant" />

      <PhotoGrid photos={photos} onPress={setLightboxIndex} />

      {hasNextPage && (
        <TouchableOpacity
          onPress={onLoadMore}
          disabled={isFetchingNextPage}
          activeOpacity={0.7}
          className="items-center py-4 hover:bg-ash-3 hover:rounded-lg"
        >
          <Text className="text-primary text-sm font-medium">
            {t(isFetchingNextPage ? "LABEL_LOADING" : "BTN_LOAD_MORE")}
          </Text>
        </TouchableOpacity>
      )}

      <View className="h-4" />

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </View>
  );
};

export default ProfilePhotoGrid;
